import type { FC } from "react";
import { useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Combobox, IconButton, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type {
    DropdownOnClickProps,
    IAiTrackerTableRow,
    MenuItemType,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { getMenuItems } from "app/pages/digital-marketing/generative-ai/llm-tracker/menu/consts";

interface ITableRowDropdownProps {
    row: IAiTrackerTableRow;
    onClick: (props: DropdownOnClickProps, row: IAiTrackerTableRow) => void;
    isSubscribePending?: boolean;
}

export const TableRowDropdown: FC<ITableRowDropdownProps> = ({ row, onClick, isSubscribePending }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const menuItems = getMenuItems({
        canEdit: row.canEdit,
        canDelete: row.canDelete,
        campaign: row,
        isSubscribePending,
    });

    const onItemClick = (id: MenuItemType): void => {
        setIsOpen(false);
        onClick?.({ id }, row);
    };

    return (
        <Combobox
            width="auto"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            data-automation={`homepage-table-row-menu-${row.id}`}
        >
            <Combobox.Trigger>
                <Flex width="100%">
                    <Tooltip
                        className="PlainTooltip-element plainTooltip-more-action-monitor-keyword-groups"
                        content={translate("global.moreActions")}
                    >
                        <span>
                            <IconButton
                                id={`moreActions-${row.id}`}
                                iconName="dots-more"
                                type="flat"
                                iconSize="20px"
                                height="34px"
                                width="34px"
                            />
                        </span>
                    </Tooltip>
                </Flex>
            </Combobox.Trigger>
            <Combobox.Content>
                {Object.entries(menuItems).map(([key, { icon: Icon, ...item }]) => (
                    <Combobox.Option
                        key={key}
                        id={key}
                        textValue={item.text}
                        isDisabled={!item.isEnabled}
                        onClick={() => onItemClick(key as MenuItemType)}
                        data-automation={`homepage-table-row-menu-item-${key}`}
                    >
                        <Flex
                            alignItems="center"
                            gap="8px"
                        >
                            <Icon
                                size="sm"
                                color={item.isEnabled ? colorsPalettes.carbon[200] : colorsPalettes.midnight[50]}
                            />
                            <Combobox.OptionLabel>{item.text}</Combobox.OptionLabel>
                        </Flex>
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};
